/**
 * IndexedDB wrapper for persistent storage of teams, sprites, and replays.
 * Uses raw IndexedDB API (no external dependency) with Promise-based helpers.
 */

import { DB_NAME, DB_STORES } from "../config/constants";

const DB_VERSION = 1;

let dbInstance: IDBDatabase | null = null;

/** Open (or create) the IndexedDB database. */
function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DB_STORES.TEAMS)) {
        db.createObjectStore(DB_STORES.TEAMS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(DB_STORES.SPRITES)) {
        db.createObjectStore(DB_STORES.SPRITES);
      }
      if (!db.objectStoreNames.contains(DB_STORES.REPLAYS)) {
        db.createObjectStore(DB_STORES.REPLAYS, { keyPath: "createdAt" });
      }
    };
  });
}

/** Get a transaction for a store with the given mode. */
async function getTx(
  store: string,
  mode: IDBTransactionMode,
): Promise<IDBObjectStore> {
  const db = await openDB();
  return db.transaction(store, mode).objectStore(store);
}

/** Put a value into a store by key. */
export async function dbPut(
  store: string,
  value: unknown,
  key?: IDBValidKey,
): Promise<void> {
  const os = await getTx(store, "readwrite");
  return new Promise((resolve, reject) => {
    const req = key !== undefined ? os.put(value, key) : os.put(value);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Get a value from a store by key. */
export async function dbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const os = await getTx(store, "readonly");
  return new Promise((resolve, reject) => {
    const req = os.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

/** Get all values from a store. */
export async function dbGetAll<T>(store: string): Promise<T[]> {
  const os = await getTx(store, "readonly");
  return new Promise((resolve, reject) => {
    const req = os.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

/** Get all keys from a store. */
export async function dbGetAllKeys(store: string): Promise<IDBValidKey[]> {
  const os = await getTx(store, "readonly");
  return new Promise((resolve, reject) => {
    const req = os.getAllKeys();
    req.onsuccess = () => resolve(req.result as IDBValidKey[]);
    req.onerror = () => reject(req.error);
  });
}

/** Delete a value from a store by key. */
export async function dbDelete(store: string, key: IDBValidKey): Promise<void> {
  const os = await getTx(store, "readwrite");
  return new Promise((resolve, reject) => {
    const req = os.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Clear all values from a store. */
export async function dbClear(store: string): Promise<void> {
  const os = await getTx(store, "readwrite");
  return new Promise((resolve, reject) => {
    const req = os.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}